import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  Upload,
  Trash2,
  Search,
  Download,
  Grid,
  List,
  MoreVertical,
  Star,
  Check,
  X,
  File,
  Image,
  FileSpreadsheet,
  Folder,
  Shield,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import MainHeader from "../MainHeader";
import { useAuth } from "../hooks/useAuth";

const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30MB

const DeepResearchFileSystem = () => {
  const { api } = useAuth(); // baseURL already includes "/api/v1"
  const fileInputRef = useRef(null);

  // Read URL search params for state persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") || "grid";
  const initialQuery = searchParams.get("sq") || "";
  const initialFilter = searchParams.get("ft") || "all";
  const initialSort = searchParams.get("sort") || "date";

  const [viewMode, setViewMode] = useState(initialView);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [filterType, setFilterType] = useState(initialFilter);
  const [sortBy, setSortBy] = useState(initialSort);
  const [documents, setDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showDropdown, setShowDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Update URL searchParams whenever relevant state changes
  useEffect(() => {
    const params = {};
    if (viewMode && viewMode !== "grid") params.view = viewMode;
    if (searchQuery) params.sq = searchQuery;
    if (filterType && filterType !== "all") params.ft = filterType;
    if (sortBy && sortBy !== "date") params.sort = sortBy;
    setSearchParams(params, { replace: true });
  }, [viewMode, searchQuery, filterType, sortBy, setSearchParams]);

  // Fetch file list from backend whenever searchQuery or sortBy changes
  useEffect(() => {
    const fetchFiles = async () => {
      setIsLoading(true);
      try {
        const response = await api.get("/deep_research/fs/tablequery", {
          params: {
            sq: searchQuery,
            orderType: sortBy,
            p: 1,
          },
        });
        const viewFiles = response.data.data || [];
        // Map backend objects to frontend shape
        console.log(viewFiles);
        const mapped = viewFiles.map((f) => ({
          id: f.ID || f.id, // GormModel.ID
          name: f.Name || f.name,
          size: f.Size || f.size,
          type: f.Type || f.type,
          starred: false,
          lastModified: f.created_at
            ? new Date(f.created_at).toLocaleString("ko-KR")
            : "",
        }));
        setDocuments(mapped);
        setSelectedFiles([]); // clear any selection on new fetch
      } catch (err) {
        console.error("파일 목록 조회 중 오류:", err);
        // Optionally show a toast or alert
      } finally {
        setIsLoading(false);
      }
    };

    fetchFiles();
  }, [api, searchQuery, sortBy]);

  // 파일 타입별 아이콘
  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <FileText className="w-5 h-5" />;
      case "excel":
        return <FileSpreadsheet className="w-5 h-5" />;
      case "image":
        return <Image className="w-5 h-5" />;
      case "folder":
        return <Folder className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  // 파일 타입별 색상
  const getFileColor = (type) => {
    switch (type) {
      case "pdf":
        return "text-red-500";
      case "excel":
        return "text-green-500";
      case "image":
        return "text-blue-500";
      case "folder":
        return "text-yellow-600";
      default:
        return "text-gray-500";
    }
  };

  // Upload handler with 30MB limit
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const oversized = files
      .filter((f) => f.size > MAX_FILE_SIZE_BYTES)
      .map((f) => f.name);
    if (oversized.length) {
      alert(
        `다음 파일이 30MB를 초과하여 업로드되지 않습니다:\n${oversized.join(
          "\n"
        )}`
      );
    }
    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }
    const formData = new FormData();
    validFiles.forEach((file) => formData.append("files", file));
    try {
      setIsLoading(true);
      const response = await api.post("/deep_research/fs/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const failed = response.data.failed_files || [];
      if (failed.length) {
        alert(`다음 파일 업로드에 실패했습니다:\n${failed.join("\n")}`);
      }
      // 리로드
      await refreshFiles();
    } catch (err) {
      console.error("파일 업로드 중 오류:", err);
      alert("파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  // Refresh file list (used after upload or delete)
  const refreshFiles = async () => {
    try {
      const response = await api.get("/deep_research/fs/tablequery", {
        params: {
          sq: searchQuery,
          orderType: sortBy,
          p: 1,
        },
      });
      const viewFiles = response.data.data || [];
      const mapped = viewFiles.map((f) => ({
        id: f.ID || f.id,
        name: f.Name || f.name,
        size: f.Size || f.size,
        type: f.Type || f.type,
        starred: false,
        lastModified: f.UpdatedAt
          ? new Date(f.UpdatedAt).toLocaleString("ko-KR")
          : "",
      }));
      setDocuments(mapped);
      setSelectedFiles([]);
    } catch (err) {
      console.error("파일 목록 다시 불러오기 중 오류:", err);
    }
  };

  // Delete files by IDs
  const handleDeleteFile = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`${ids.length}개 파일을 삭제하시겠습니까?`)) return;
    try {
      setIsLoading(true);
      const response = await api.post("/deep_research/fs/remove", {
        check_ids: ids.map(String),
      });
      const failed = response.data.failed_files || [];
      if (failed.length) {
        alert(`다음 파일 삭제에 실패했습니다:\n${failed.join("\n")}`);
      }
      await refreshFiles();
    } catch (err) {
      console.error("파일 삭제 중 오류:", err);
      alert("파일 삭제 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle selection
  const toggleFileSelection = (id) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  // Select all on current filtered list
  const handleSelectAll = () => {
    if (selectedFiles.length === filteredDocuments.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredDocuments.map((doc) => doc.id));
    }
  };

  // Toggle star locally
  const toggleStar = (id) => {
    setDocuments((docs) =>
      docs.map((doc) =>
        doc.id === id ? { ...doc, starred: !doc.starred } : doc
      )
    );
  };

  // Filter and sort client-side after receiving from server
  const filteredDocuments = documents
    .filter((doc) => {
      const matchesSearch = doc.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      let matchesType = true;
      if (filterType === "starred") {
        matchesType = doc.starred;
      } else if (filterType !== "all") {
        matchesType = doc.type === filterType;
      }
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "date":
          return new Date(b.lastModified) - new Date(a.lastModified);
        case "size":
          const aNum = parseFloat(a.size);
          const bNum = parseFloat(b.size);
          return aNum - bNum;
        default:
          return 0;
      }
    });

  // Total size in MB (folders excluded)
  const totalSize = documents
    .filter((doc) => doc.type !== "folder")
    .reduce((acc, doc) => {
      const num = parseFloat(doc.size);
      if (doc.size.toUpperCase().includes("MB")) {
        return acc + num;
      }
      if (doc.size.toUpperCase().includes("KB")) {
        return acc + num / 1024;
      }
      return acc;
    }, 0);

  // Handle changes to controls and sync to state
  const onViewChange = (mode) => setViewMode(mode);
  const onSearchChange = (e) => setSearchQuery(e.target.value);
  const onFilterChange = (e) => setFilterType(e.target.value);
  const onSortChange = (e) => setSortBy(e.target.value);

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* 배경 효과 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/30 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-purple-400/30 rounded-full filter blur-[120px] animate-pulse delay-700"></div>
        </div>

        <div className="relative z-10 h-screen flex flex-col">
          {/* 헤더 */}
          <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-white">
                  문서 관리 시스템
                </h1>
                <div className="flex items-center gap-4">
                  <div className="bg-white/10 px-4 py-2 rounded-lg">
                    <p className="text-xs text-white/70">사용 용량</p>
                    <p className="text-sm font-semibold text-white">
                      {totalSize.toFixed(1)} MB / 100 GB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    업로드
                  </button>
                </div>
              </div>

              {/* 검색 및 필터 */}
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={onSearchChange}
                    placeholder="파일 검색..."
                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:bg-blue/20 focus:border-white/40 transition-colors"
                  />
                </div>

                <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => onViewChange("grid")}
                    className={`p-2 rounded ${
                      viewMode === "grid" ? "bg-white/20" : "hover:bg-white/10"
                    } transition-colors`}
                  >
                    <Grid className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => onViewChange("list")}
                    className={`p-2 rounded ${
                      viewMode === "list" ? "bg-white/20" : "hover:bg-white/10"
                    } transition-colors`}
                  >
                    <List className="w-4 h-4 text-white" />
                  </button>
                </div>

                <select
                  value={filterType}
                  onChange={onFilterChange}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:bg-blue/20 transition-colors"
                >
                  <option value="all">모든 파일</option>
                </select>

                <select
                  value={sortBy}
                  onChange={onSortChange}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-dark focus:bg-blue/20 transition-colors"
                >
                  <option value="name">이름순</option>
                  <option value="date">날짜순</option>
                </select>
              </div>
            </div>
          </div>

          {/* 툴바 */}
          {selectedFiles.length > 0 && (
            <div className="bg-white/10 backdrop-blur-lg border-b border-white/20 px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 text-white hover:text-white/80"
                  >
                    {selectedFiles.length === filteredDocuments.length ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {selectedFiles.length}개 선택됨
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteFile(selectedFiles)}
                    className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 파일 목록 */}
          <div className="flex-1 overflow-auto p-6 relative">
            {isLoading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                <p className="text-white">로딩 중...</p>
              </div>
            )}

            {filteredDocuments.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-white/60">
                <FileText className="w-16 h-16 mb-4" />
                <p className="text-lg">검색 결과가 없습니다</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className={`relative group bg-white/10 backdrop-blur rounded-xl p-4 border ${
                      selectedFiles.includes(doc.id)
                        ? "border-white/40 bg-white/20"
                        : "border-white/20 hover:border-white/30 hover:bg-white/15"
                    } transition-all cursor-pointer`}
                    onClick={() => toggleFileSelection(doc.id)}
                  >
                    {/* 선택된 경우 체크 아이콘 표시 */}
                    {selectedFiles.includes(doc.id) && (
                      <Check className="absolute top-2 left-2 w-5 h-5 text-white" />
                    )}

                    {doc.starred && (
                      <Star className="absolute top-2 left-2 w-4 h-4 text-yellow-400 fill-yellow-400" />
                    )}

                    <div className={`mb-3 ${getFileColor(doc.type)}`}>
                      {getFileIcon(doc.type)}
                    </div>

                    <h3 className="text-sm font-medium text-white truncate mb-1">
                      {doc.name}
                    </h3>
                    <p className="text-xs text-white/60">
                      {doc.type === "folder" ? "-" : doc.size}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur rounded-xl border border-white/20 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left px-4 py-3">
                        <input
                          type="checkbox"
                          checked={
                            selectedFiles.length === filteredDocuments.length &&
                            filteredDocuments.length > 0
                          }
                          onChange={handleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-white/80 font-medium">
                        이름
                      </th>
                      <th className="text-left px-4 py-3 text-white/80 font-medium">
                        업로드 날짜
                      </th>
                      <th className="text-left px-4 py-3 text-white/80 font-medium">
                        크기
                      </th>
                      <th className="text-left px-4 py-3 text-white/80 font-medium">
                        유형
                      </th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map((doc) => (
                      <tr
                        key={doc.id}
                        className={`border-b border-white/10 hover:bg-white/10 transition-colors ${
                          selectedFiles.includes(doc.id) ? "bg-white/10" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedFiles.includes(doc.id)}
                            onChange={() => toggleFileSelection(doc.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={getFileColor(doc.type)}>
                              {getFileIcon(doc.type)}
                            </div>
                            <span className="text-white font-medium">
                              {doc.name}
                            </span>
                            {doc.starred && (
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-white/70 text-sm">
                          {doc.lastModified}
                        </td>
                        <td className="px-4 py-3 text-white/70 text-sm">
                          {doc.type === "folder" ? "-" : doc.size}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs px-2 py-1 rounded-full bg-white/10 ${getFileColor(
                              doc.type
                            )}`}
                          >
                            {doc.type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => handleDeleteFile([doc.id])}
                            className="p-1 text-red-400 hover:bg-red-400/10 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 하단 정보 */}
          <div className="bg-white/10 backdrop-blur-lg border-t border-white/20 px-6 py-3">
            <div className="flex items-center justify-between text-white/70 text-sm">
              <div className="flex items-center gap-6">
                <span>{filteredDocuments.length}개 항목</span>
                <span>{selectedFiles.length}개 선택됨</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>모든 파일은 AES-256으로 암호화됩니다</span>
              </div>
            </div>
          </div>
        </div>

        {/* 숨겨진 파일 입력 */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileUpload}
          className="hidden"
          accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg"
        />
      </div>
    </>
  );
};

export default DeepResearchFileSystem;
